import React from 'react';
import { PDFViewer } from '@odo/shared-ui';
import { useContentFileUrl } from '../hooks/useContentFiles';

interface ResourcePdfViewerProps {
    /** storage.file id of the PDF, from HelpDocumentData.pdfFileId. */
    fileId: string | null | undefined;
    showControls?: boolean;
    className?: string;
}

/**
 * Renders a resources-section PDF straight from GCS. Resolves the fileId to a
 * short-lived signed URL, then hands it to the shared PDFViewer in direct mode
 * (`proxyPath={null}`) — nothing streams through the API any more, so there is
 * no proxy path and no auth token to pass.
 */
export const ResourcePdfViewer: React.FC<ResourcePdfViewerProps> = ({
    fileId,
    showControls = true,
    className
}) => {
    const { url, isLoading, error } = useContentFileUrl(fileId);

    // A help document with no pdfFileId failed the GCS migration and has no file
    // to show. Say so plainly instead of rendering an empty viewer.
    if (!fileId) {
        return (
            <div className={`flex items-center justify-center ${className ?? ''}`}>
                <p className="text-sm text-gray-500">This document is not available yet.</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={`flex items-center justify-center ${className ?? ''}`}>
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
            </div>
        );
    }

    if (error || !url) {
        return (
            <div className={`flex items-center justify-center ${className ?? ''}`}>
                <p className="text-sm text-red-500">{error ?? 'Unable to load this document.'}</p>
            </div>
        );
    }

    return (
        <PDFViewer
            pdfUrl={url}
            proxyPath={null}
            showControls={showControls}
            className={className}
        />
    );
};
